import { defineStore } from 'pinia';
import type { MigrationDeployment, MigrationDeploymentAdd } from '@schema';
import api from '@/common/utils/api';
import useCommonStore from '@/common/store/pinia/common';
import mapper, { MappedDeployment } from '@/common/store/pinia/deployments/deploymentsMapper';
import { getDeploymentsUrl, getMigrationsUrl } from '@/common/store/pinia/maasUrls';

export const useDeploymentsStore = defineStore('deployments', () => {
  const commonStore = useCommonStore<MappedDeployment, MappedDeployment>({
    getUrl: getMigrationsUrl,
    itemKey: 'id',
    updateMethod: 'patch',
    dataChangeMode: 'direct'
  })();

  const {
    getItemById,
    getItemsByView,
    addViewList,
    setData,
    removeItem,
    pushData
  } = commonStore;

  const fetchItem = (migrationId: string, deploymentId: string) => {
    const view = `migration-${migrationId}`;
    return api.get<MigrationDeployment>(`${getDeploymentsUrl(migrationId)}/${deploymentId}`)
      .then((response) => {
        pushData([mapper(response.data, migrationId)], view);
      });
  };

  const fetchItems = (migrationId?: string) => {
    if (!migrationId) {
      return Promise.resolve({ data: [] as MappedDeployment[] });
    }
    const view = `migration-${migrationId}`;
    addViewList(view);
    return api.get<{ data: MigrationDeployment[] }>(getDeploymentsUrl(migrationId))
      .then((response) => {
        setData(response.data.data.map(d => mapper(d, migrationId)), view);
      })
      .catch(() => {
        setData([], view);
      })
      .then(() => ({ data: getItemsByView.value(view) }));
  };

  const cancelDeployment = (migrationId: string, id: string) => api.post(`${getDeploymentsUrl(migrationId)}/${id}/cancel`)
    .then(() => fetchItems(migrationId));

  const recreateDeployment = (migrationId: string, deployment: MappedDeployment) => {
    const body: MigrationDeploymentAdd = {
      name: deployment.name,
      clone_disks: deployment.deploymentOpts.cloneDisks,
      force: deployment.deploymentOpts.force,
      os_morphing: deployment.deploymentOpts.osMorphing,
      save_snapshot: deployment.deploymentOpts.saveSnapshot,
      start: deployment.deploymentOpts.start
    };
    return api.post(getDeploymentsUrl(migrationId), body)
      .then(() => fetchItems(migrationId));
  };

  const deleteDeployment = (migrationId: string, id: string) => api.delete(`${getDeploymentsUrl(migrationId)}/${id}`)
    .then(() => {
      removeItem(id);
    });

  const deleteItems = (migrationId: string, ids: string[]) => Promise.all(ids.map(id => deleteDeployment(migrationId, id)));

  const createDeployment = (migrationId: string) => {
    const view = `migration-${migrationId}`;
    const body: MigrationDeploymentAdd = {
      clone_disks: false,
      force: false,
      os_morphing: false,
      save_snapshot: false,
      start: false
    };
    return api.post<MigrationDeployment>(getDeploymentsUrl(migrationId), body)
      .then((response) => {
        pushData([mapper(response.data, migrationId)], view);
        return { data: getItemById.value(response.data?.id) };
      });
  };

  return {
    ...commonStore,
    fetchItem,
    fetchItems,
    cancelDeployment,
    recreateDeployment,
    deleteDeployment,
    deleteItems,
    createDeployment
  };
});

export default useDeploymentsStore;
